import {
  CheckCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  UndoOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Descriptions,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";

import {
  IPartNumberTransferDetailDto,
  IPartNumberTransferExport,
} from "../../../models/Warehouse";
import {
  useCompleteTransferWarehouseDetail,
  useExportFromTransfer,
  useGetTransferWarehouseDetail,
} from "../../../query/WarehouseQuery";
import { notify } from "../../../utils/toast";

const { Title, Text } = Typography;

interface ModalViewTransferProps {
  open: boolean;
  onClose: () => void;
  transferId: number | null;
}

const ModalViewTransfer: React.FC<ModalViewTransferProps> = ({
  open,
  onClose,
  transferId,
}) => {
  const { data: transferDetail, isLoading } = useGetTransferWarehouseDetail(
    transferId!,
  );
  const isCompleted = transferDetail?.isCompleted;
  const isExported = transferDetail?.isExported;
  const completeTransferWarehouseDetail = useCompleteTransferWarehouseDetail();
  const exportTransferWarehouse = useExportFromTransfer();

  const [actualQuantities, setActualQuantities] = useState<
    Record<number, number>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setActualQuantities({});
    }
  }, [open, transferId]);

  const handleAutoFill = () => {
    if (!transferDetail) return;
    const newQuantities: Record<number, number> = {};
    transferDetail.partNumberTransferDetails.forEach((item) => {
      newQuantities[item.id] = item.reportSAQuantity;
    });
    setActualQuantities(newQuantities);
  };

  const handleReset = () => {
    setActualQuantities({});
  };

  const handleQuantityChange = (id: number, value: number | null) => {
    setActualQuantities((prev) => {
      if (value === null || value === undefined) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [id]: value,
      };
    });
  };

  const handleExport = async () => {
    if (!transferDetail || !transferId) return;

    const allItems = transferDetail.partNumberTransferDetails;

    const missingQuantities = allItems.filter(
      (item) => actualQuantities[item.id] === undefined,
    );

    if (missingQuantities.length > 0) {
      notify.error(
        "Thiếu số lượng xuất thực tế",
        `Vui lòng nhập số lượng xuất thực tế cho tất cả ${allItems.length} linh kiện!`,
      );
      return;
    }

    const invalidQuantities = allItems.filter(
      (item) => actualQuantities[item.id] > item.transferQuantity,
    );

    if (invalidQuantities.length > 0) {
      notify.error(
        "Số lượng xuất thực tế không hợp lệ",
        `Số lượng xuất thực tế không được vượt quá số lượng chuyển!`,
      );
      return;
    }

    try {
      const exportData: IPartNumberTransferExport = {
        exportTransfers: allItems.map((item) => ({
          partNumberId: item.partNumber.id,
          exportQuantity: actualQuantities[item.id],
        })),
      };

      const response = await exportTransferWarehouse.mutateAsync({
        id: transferId,
        data: exportData,
      });
      const downloadExcel = response.fileUrl;
      if (downloadExcel) {
        window.open(downloadExcel);
      }
      notify.success("Xuất kho thành công");
      setActualQuantities({});
      onClose();
    } catch (error) {
      notify.error("Có lỗi xảy ra");
    }
  };
  const handleComplete = async (id: number) => {
    try {
      await completeTransferWarehouseDetail.mutateAsync(id);
      notify.success("Xác nhận đã nhận hàng thành công");
    } catch (error) {
      notify.error("Có lỗi xảy ra");
    }
  };

  const handleDownloadTemplate = () => {
    if (!transferDetail) return;

    const templateData = transferDetail.partNumberTransferDetails.map(
      (item, index) => ({
        STT: index + 1,
        "Part Number": item.partNumber.partNumber,
        "Dự án": item.partNumber.project,
        "Mã dự án": item.partNumber.projectCode,
        AWB: item.partNumber.awb,
        "Kho hiện tại":
          item.partNumber.location?.warehouse?.warehouseCode || "",
        "Vị trí hiện tại": item.partNumber.location?.code || "",
        "Số lượng chuyển": item.transferQuantity,
        "Số lượng SA báo": item.reportSAQuantity,
        "Đơn vị": item.partNumber.unit,
        "Số lượng xuất thực tế": "",
      }),
    );

    const ws = XLSX.utils.json_to_sheet(templateData);

    ws["!cols"] = [
      { wch: 5 }, // STT
      { wch: 20 }, // Part Number
      { wch: 15 }, // Dự án
      { wch: 12 }, // Mã dự án
      { wch: 15 }, // AWB
      { wch: 15 }, // Kho hiện tại
      { wch: 15 }, // Vị trí hiện tại
      { wch: 15 }, // Số lượng chuyển
      { wch: 15 }, // Số lượng SA báo
      { wch: 10 }, // Đơn vị
      { wch: 20 }, // Số lượng xuất thực tế
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    const fileName = `transfer_template_${transferDetail.transferName}_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);

    notify.success("Tải file Excel mẫu thành công");
  };

  const handleUploadExcel = (file: File) => {
    if (!transferDetail) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

        if (jsonData.length === 0) {
          notify.error("File Excel không có dữ liệu");
          return;
        }

        const firstRow = jsonData[0];
        if (!("Số lượng xuất thực tế" in firstRow)) {
          notify.error(
            'File Excel thiếu cột "Số lượng xuất thực tế"',
            "Vui lòng sử dụng file mẫu đã tải xuống",
          );
          return;
        }

        const newQuantities: Record<number, number> = {};
        let validCount = 0;
        let errorCount = 0;
        jsonData.forEach((row, index) => {
          const partNumber = row["Part Number"];
          const actualQty = row["Số lượng xuất thực tế"];
          if (!partNumber) return;

          const matchingItem = transferDetail.partNumberTransferDetails.find(
            (item) => item.partNumber.partNumber === partNumber,
          );
          if (matchingItem && actualQty !== "" && actualQty != null) {
            const qty = Number(actualQty);
            if (isNaN(qty) || qty < 0) {
              errorCount++;
              notify.warning(
                `Dòng ${index + 2}: Số lượng không hợp lệ cho ${partNumber}`,
              );
              return;
            }
            if (qty > matchingItem.transferQuantity) {
              errorCount++;
              notify.warning(
                `Dòng ${index + 2}: Số lượng ${qty} vượt quá số lượng chuyển ${matchingItem.transferQuantity} cho ${partNumber}`,
              );
              return;
            }

            newQuantities[matchingItem.id] = qty;
            validCount++;
          }
        });
        if (validCount > 0) {
          setActualQuantities(newQuantities);
          notify.success(
            `Đã nhập ${validCount} số lượng thực tế từ Excel${errorCount > 0 ? ` (${errorCount} lỗi)` : ""}`,
          );
        } else {
          notify.error("Không có dữ liệu hợp lệ nào được nhập");
        }
      } catch (error) {
        notify.error("Lỗi khi đọc file Excel", "Vui lòng kiểm tra lại file");
      }
    };

    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadExcel(file);
      e.target.value = "";
    }
  };

  const columns: TableColumnsType<IPartNumberTransferDetailDto> = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_: unknown, __: any, index: number) => index + 1,
    },

    {
      title: "Part Number",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
      width: 100,
      render: (text: string) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: "Dự án ",
      dataIndex: ["partNumber", "project"],
      key: "project",
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Mã dự án",
      dataIndex: ["partNumber", "projectCode"],
      key: "projectCode",
      width: 80,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "AWB",
      dataIndex: ["partNumber", "awb"],
      key: "projectCode",
      width: 80,
    },
    {
      title: "Kho hiện tại",
      dataIndex: ["partNumber", "location", "warehouse", "warehouseCode"],
      key: "currentLocation",
      width: 80,
      align: "center",
    },

    {
      title: "Vị trí hiện tại",
      dataIndex: ["partNumber", "location", "code"],
      key: "currentLocation",
      width: 80,
      align: "center",
    },
    {
      title: "Số lượng chuyển",
      dataIndex: "transferQuantity",
      key: "transferQuantity",
      width: 80,
      align: "center",
      render: (value: number) => (
        <Text strong style={{ color: "#1890ff", fontSize: 16 }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Số lượng SA báo",
      dataIndex: "reportSAQuantity",
      key: "reportSAQuantity",
      width: 80,
      align: "center",
      render: (value: number) => (
        <Text strong style={{ color: "#52c41a", fontSize: 16 }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Đơn vị",
      dataIndex: ["partNumber", "unit"],
      key: "unit",
      width: 80,
      align: "center",
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: "Số lượng xuất thực tế",
      dataIndex: "exportQuantity",
      key: "exportQuantity",
      width: 150,
      align: "center",
      render: (value: number, record: IPartNumberTransferDetailDto) =>
        value != null ? (
          <Text>{value}</Text>
        ) : (
          <InputNumber
            min={0}
            max={record.transferQuantity}
            value={actualQuantities[record.id]}
            onChange={(value) => handleQuantityChange(record.id, value)}
            disabled={!isCompleted}
            style={{ width: "100%" }}
            placeholder="Nhập số lượng"
          />
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={2400}
      footer={null}
      closeIcon={<CloseOutlined />}
      title={
        <Space>
          <FileTextOutlined style={{ fontSize: 20, color: "#1890ff" }} />
          <Text>Chi tiết phiếu chuyển kho</Text>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        {transferDetail && (
          <div style={{ padding: "16px 0" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Space>
                <Popconfirm
                  title="Xác nhận nhận hàng"
                  description="Bạn có chắc chắn đã nhận hàng?"
                  onConfirm={() => handleComplete(transferId!)}
                  okButtonProps={{
                    loading: completeTransferWarehouseDetail.isPending,
                  }}
                >
                  <Button type="primary" disabled={transferDetail.isCompleted}>
                    <CheckCircleOutlined />
                    {transferDetail.isCompleted
                      ? "Đã nhận hàng"
                      : "Xác nhận đã nhận hàng"}
                  </Button>
                </Popconfirm>
                {transferDetail.isCompleted && !transferDetail.isExported && (
                  <>
                    <Button
                      type="default"
                      icon={<ThunderboltOutlined />}
                      onClick={handleAutoFill}
                    >
                      Tự động điền (Số lượng SA báo)
                    </Button>
                    <Button
                      icon={<UndoOutlined />}
                      onClick={handleReset}
                      danger
                    >
                      Huỷ tự động điền (Reset)
                    </Button>
                    <Button
                      type="default"
                      icon={<FileExcelOutlined />}
                      onClick={handleDownloadTemplate}
                      style={{ color: "#52c41a", borderColor: "#52c41a" }}
                    >
                      Tải Excel mẫu
                    </Button>
                    <Button
                      icon={<UploadOutlined />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Excel
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </>
                )}
              </Space>

              <Button
                type="primary"
                disabled={!isCompleted || isExported}
                icon={<ExportOutlined />}
                onClick={handleExport}
              >
                {isExported ? (
                  <p>
                    Xuất lúc{" "}
                    {dayjs(transferDetail.exportedAt).format(
                      "DD/MM/YYYY HH:mm:ss",
                    )}
                  </p>
                ) : (
                  "Xuất kho"
                )}
              </Button>
            </div>

            <Card
              style={{ marginBottom: 24 }}
              styles={{ body: { padding: 16 } }}
            >
              <Descriptions
                bordered
                column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }}
                size="small"
              >
                <Descriptions.Item label="Tên phiếu" span={2}>
                  <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                    {transferDetail.transferName}
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item label="Thời gian tạo">
                  <Text>
                    {new Date(transferDetail.createdAt).toLocaleString("vi-VN")}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Vị trí đích">
                  <Tag
                    color="green"
                    icon={<EnvironmentOutlined />}
                    style={{ fontSize: 14, padding: "4px 12px" }}
                  >
                    {transferDetail.targetLocation.code}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Kho đích">
                  <Tag
                    color="blue"
                    style={{ fontSize: 14, padding: "4px 12px" }}
                  >
                    {transferDetail.targetLocation.warehouse.warehouseCode}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Ghi chú" span={2}>
                  <Text type="secondary">
                    {transferDetail.note || "Chưa có ghi chú"}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title={
                <Title level={5} style={{ margin: 0 }}>
                  Danh sách
                </Title>
              }
              styles={{ body: { padding: 0 } }}
            >
              <Table
                columns={columns}
                dataSource={transferDetail.partNumberTransferDetails}
                rowKey="id"
                pagination={false}
                scroll={{ x: 1200, y: 400 }}
                size="small"
              />
            </Card>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ModalViewTransfer;
